import { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { CrudMode } from '@ohpen/enso-ui';
import { IconUsers } from '@tabler/icons-react';

import { AssistantsList } from '@pmp/components';
import {
  AssistantCollaboration,
  AssistantPermissionType,
  FieldType,
  firmIntermediariesGetFirmAssistants,
  FirmIntermediaryByRole,
} from '@pmp/api';
import { CollaborationRow } from '../../assistants/interfaces';

import { CrudFormCard } from '../../firms/components/CrudFormCard';
import { TabProps } from '../interfaces';
import { TabContent } from './TabContent';

export const AssistantsTab: React.FC<TabProps> = ({ form, mode, selectsOptions, setDisableSave }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;

  const [availableAssistants, setAvailableAssistants] = useState<FirmIntermediaryByRole[]>([]);
  const [isFetchingAvailableAssistants, setIsFetchingAvailableAssistants] = useState(false);
  const [collaborationRows, setCollaborationRows] = useState<CollaborationRow[]>([]);
  const initializedRef = useRef(false);
  const hasFetchedRef = useRef(false);

  const watchedFirmId = Form.useWatch('firmId', form) as string | undefined;
  const watchedIntermediaryId = Form.useWatch('id', { form, preserve: true }) as string | undefined;

  useEffect(() => {
    initializedRef.current = false;
    hasFetchedRef.current = false;
    setCollaborationRows([]);
    if (!watchedFirmId) {
      setAvailableAssistants([]);
      return;
    }
    let cancelled = false;
    setIsFetchingAvailableAssistants(true);
    firmIntermediariesGetFirmAssistants({ path: { firmId: watchedFirmId } })
      .then(({ data }) => { if (!cancelled) setAvailableAssistants(data ?? []); })
      .catch(() => { })
      .finally(() => { if (!cancelled) { hasFetchedRef.current = true; setIsFetchingAvailableAssistants(false); } });
    return () => { cancelled = true; };
  }, [watchedFirmId]);

  useEffect(() => {
    if (initializedRef.current || !hasFetchedRef.current) return;
    initializedRef.current = true;
    const existing = (form?.getFieldValue('assistants') as AssistantCollaboration[] | null) ?? [];
    if (existing.length > 0) {
      const rows = existing
        .map(({ assistantId, permissionType, displayName }) => {
          const a = availableAssistants.find(a => a.id === assistantId);
          return assistantId ? { assistantId, name: displayName ?? '', collaborationType: permissionType ?? '', roles: a?.roles, email: a?.email } : null;
        })
        .filter(Boolean) as CollaborationRow[];
      setCollaborationRows(rows);
    }
  }, [isFetchingAvailableAssistants, availableAssistants, form]);

  const collaborationOptions = useMemo(
    () => (selectsOptions[FieldType.ASSISTANT_PERMISSION_TYPE] ?? []),
    [selectsOptions],
  );

  const handleCollaborationRowsChange = (rows: CollaborationRow[]) => {
    setCollaborationRows(rows);
    form?.setFieldsValue({
      assistants: rows.map(r => ({ assistantId: r.assistantId, permissionType: r.collaborationType as AssistantPermissionType })),
    });
    setDisableSave?.(false);
  };

  return (
    <TabContent noScroll>
      <CrudFormCard collapsible={readonly} loading={isFetchingAvailableAssistants} icon={<IconUsers />} title={t('lender.brokers.labels.assistants')}>
        <AssistantsList
          readonly={readonly}
          collaborationMode={!readonly}
          availableAssistants={availableAssistants}
          currentIntermediaryId={watchedIntermediaryId}
          collaborationRows={collaborationRows}
          onCollaborationRowsChange={handleCollaborationRowsChange}
          roleOptions={selectsOptions[FieldType.USER_ROLE_TYPE]}
          collaborationOptions={collaborationOptions}
        />
      </CrudFormCard>
    </TabContent>
  );
};
